import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const ResetPassword = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const emailParam = searchParams.get("email") || "";

  const [email, setEmail] = useState(emailParam);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidationError("Reset token is missing. Please try the reset link again.");
      setIsValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await api.get("/auth/reset-password/validate", {
          params: { token, email: emailParam },
        });

        setEmail(response.data.email || emailParam);
        setValidationError("");
      } catch (error) {
        console.error("Reset token validation failed:", error);
        const message =
          error.response?.data?.message ||
          "This reset link is invalid or has expired. Please request a new one.";
        setValidationError(message);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, emailParam]);

  if (user) {
    return <Navigate to="/productivity-coaching" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    if (!password || !confirmPassword) {
      setSubmitError("Please complete both password fields.");
      return;
    }

    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/auth/reset-password", { token, password });

      if (email) {
        const loginResult = await login(email, password);

        if (!loginResult.success) {
          setSubmitError(
            loginResult.error ||
              "Password updated, but we couldn't log you in automatically. Please try signing in."
          );
          setIsSubmitting(false);
          return;
        }

        navigate("/productivity-coaching", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    } catch (error) {
      console.error("Password reset failed:", error);
      const message =
        error.response?.data?.message ||
        "We couldn't reset your password. Please request a new reset link.";
      setSubmitError(message);
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (isValidating) {
      return (
        <div className="text-center text-slate-500">
          Validating your reset link...
        </div>
      );
    }

    if (validationError) {
      return (
        <div className="text-center">
          <p className="text-red-600 font-medium">{validationError}</p>
          <p className="text-slate-500 mt-4">
            Need a new link? <Link to="/forgot-password" className="text-brand-primary font-semibold">Request another reset email.</Link>
          </p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="reset-email" className="block text-sm font-semibold text-slate-700 mb-2">
            Email Address
          </label>
          <input
            id="reset-email"
            type="email"
            value={email}
            disabled
            className="block w-full px-4 py-3 border border-slate-200 text-slate-500 bg-slate-50 rounded-lg"
          />
        </div>

        <div>
          <label htmlFor="new-password" className="block text-sm font-semibold text-slate-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg brand-focus hover:border-slate-400 brand-transition"
              placeholder="Enter a new password"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 px-3 text-sm text-brand-primary font-medium"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-semibold text-slate-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg brand-focus hover:border-slate-400 brand-transition"
              placeholder="Re-enter your new password"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 px-3 text-sm text-brand-primary font-medium"
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {submitError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-4 brand-gradient text-white font-semibold rounded-lg brand-transition brand-hover-lift brand-focus disabled:opacity-50 disabled:transform-none"
        >
          {isSubmitting ? "Updating password..." : "Update password"}
        </button>
      </form>
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)",
      }}
    >
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="brand-card p-8 brand-shadow-lg">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 brand-gradient rounded-2xl flex items-center justify-center brand-shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="hellopc-wordmark-large text-center mb-2 hellopc-logo">
              <span className="hello">Hello</span>
              <span className="pc">PC</span>
            </h1>
            <h2 className="text-lg font-semibold text-slate-700">
              Choose a new password
            </h2>
            <p className="text-slate-500 mt-2">
              Make sure it's secure and something you'll remember.
            </p>
          </div>

          {renderContent()}

          <div className="mt-6 text-center text-sm text-slate-500">
            <Link to="/login" className="text-brand-primary font-medium">
              Return to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
